import React, { useEffect, useMemo, useRef, useState } from 'react';
import { UserCheck, UserX, Edit3, ChevronLeft, ChevronRight, Mail } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api';
import EditUsuarioModal from './EditUsuarioModal';

const ROL_LABEL = { admin: 'Administrador', asistente: 'Asistente', cliente: 'Cliente' };

export default function UsuariosPanel() {
  const [usuarios, setUsuarios] = useState([]);
  const [busqueda, setBusqueda] = useState('');
  const [cargando, setCargando] = useState(true);
  const [editando, setEditando] = useState(null);
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(20);
  const debounceRef = useRef(null);

  const cargar = (search) => {
    setCargando(true);
    api.get('/usuarios/', { params: { search: search || undefined } })
      .then((res) => setUsuarios(res.data.results || res.data))
      .catch(() => toast.error('No se pudieron cargar los usuarios.'))
      .finally(() => setCargando(false));
  };

  useEffect(() => { cargar(); }, []);

  const onBuscar = (valor) => {
    setBusqueda(valor);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { cargar(valor); setPagina(1); }, 300);
  };

  useEffect(() => setPagina(1), [porPagina]);

  const totalPaginas = Math.max(1, Math.ceil(usuarios.length / porPagina));
  const paginaActual = Math.min(pagina, totalPaginas);
  const usuariosPaginados = useMemo(
    () => usuarios.slice((paginaActual - 1) * porPagina, paginaActual * porPagina),
    [usuarios, paginaActual, porPagina]
  );

  const toggleActivo = async (usuario) => {
    try {
      const res = await api.patch(`/usuarios/${usuario.id}/`, { is_active: !usuario.is_active });
      setUsuarios((lista) => lista.map((u) => (u.id === usuario.id ? { ...u, is_active: res.data.is_active } : u)));
      toast.success(res.data.is_active ? 'Usuario habilitado.' : 'Usuario deshabilitado.');
    } catch (err) {
      toast.error(err.response?.data?.is_active || 'No se pudo actualizar el usuario.');
    }
  };

  const reenviarActivacion = async (usuario) => {
    try {
      await api.post('/auth/reenviar-activacion/', { user_id: usuario.id });
      toast.success(`Correo de activación reenviado a ${usuario.email}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'No se pudo reenviar el correo.');
    }
  };

  const onGuardado = (updated) => {
    setUsuarios((lista) => lista.map((u) => (u.id === updated.id ? { ...u, ...updated } : u)));
    setEditando(null);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-4)' }}>
        <h2 style={{ marginBottom: 0 }}>Usuarios</h2>
        <input
          value={busqueda}
          onChange={(e) => onBuscar(e.target.value)}
          placeholder="Buscar por nombre, correo o casillero..."
          style={{ maxWidth: '320px' }}
        />
      </div>

      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>Nombre</th>
              <th>Correo</th>
              <th>Rol</th>
              <th>Casillero</th>
              <th>Estado</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {cargando ? (
              <tr><td colSpan={6} className="text-secondary">Cargando...</td></tr>
            ) : usuariosPaginados.length === 0 ? (
              <tr><td colSpan={6} className="text-secondary">Sin resultados.</td></tr>
            ) : usuariosPaginados.map((u) => (
              <tr key={u.id}>
                <td>{`${u.first_name} ${u.last_name}`.trim() || u.username}</td>
                <td>{u.email}</td>
                <td>{ROL_LABEL[u.rol] || '—'}</td>
                <td className="mono">{u.codigo_casillero || '—'}</td>
                <td>
                  <span className={`badge ${u.is_active ? 'badge-success' : 'badge-error'}`}>
                    {u.is_active ? 'Activo' : 'Deshabilitado'}
                  </span>
                </td>
                <td className="text-right">
                  <div style={{ display: 'flex', gap: 'var(--spacing-2)', justifyContent: 'flex-end' }}>
                    <button className="btn-ghost btn-sm" onClick={() => setEditando(u)}>
                      <Edit3 size={14} /> Editar
                    </button>
                    {!u.is_active && (
                      <button className="btn-ghost btn-sm" onClick={() => reenviarActivacion(u)}>
                        <Mail size={14} /> Reenviar activación
                      </button>
                    )}
                    <button className="btn-ghost btn-sm" onClick={() => toggleActivo(u)}>
                      {u.is_active ? <><UserX size={14} /> Deshabilitar</> : <><UserCheck size={14} /> Habilitar</>}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {usuarios.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'var(--spacing-4)', flexWrap: 'wrap', gap: 'var(--spacing-3)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
            <span className="text-sm text-secondary">Mostrar</span>
            <select value={porPagina} onChange={(e) => setPorPagina(Number(e.target.value))} style={{ width: 'auto', minWidth: '70px' }}>
              {[10, 20, 30, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            <span className="text-sm text-secondary">por página</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
            <span className="text-sm text-secondary">
              {(paginaActual - 1) * porPagina + 1}–{Math.min(paginaActual * porPagina, usuarios.length)} de {usuarios.length}
            </span>
            <button className="btn-ghost btn-sm" disabled={paginaActual <= 1} onClick={() => setPagina(paginaActual - 1)}>
              <ChevronLeft size={16} />
            </button>
            <button className="btn-ghost btn-sm" disabled={paginaActual >= totalPaginas} onClick={() => setPagina(paginaActual + 1)}>
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {editando && (
        <EditUsuarioModal
          usuario={editando}
          onClose={() => setEditando(null)}
          onGuardado={onGuardado}
        />
      )}
    </div>
  );
}
